import { useEffect, useState } from 'react';
import { useUser } from '../context/UserContext.tsx';
import { Users } from '../models/User.tsx';
import {
    DEFAULT_ALLOW_ACCESS_TO_ALL_LIMITS,
    DEFAULT_ALLOW_CREDIT_DEBIT_ADJUSTMENTS,
    DEFAULT_ALLOW_FREE_PLAY,
    getAgentUserLimits,
} from '../services/player/SportService.tsx';

export type AgentPermissions = {
    allowFreePlay: boolean;
    allowAccessToAllLimits: boolean;
    allowCreditDebitAdjustments: boolean;
};

const DEFAULT_AGENT_PERMISSIONS: AgentPermissions = {
    allowFreePlay: DEFAULT_ALLOW_FREE_PLAY,
    allowAccessToAllLimits: DEFAULT_ALLOW_ACCESS_TO_ALL_LIMITS,
    allowCreditDebitAdjustments: DEFAULT_ALLOW_CREDIT_DEBIT_ADJUSTMENTS,
};

function permissionsFromUser(user?: Pick<Users, 'userType' | 'allowFreePlay' | 'allowAccessToAllLimits' | 'allowCreditDebitAdjustments'> | null): AgentPermissions {
    if (!user || user.userType !== 'subAgent') return DEFAULT_AGENT_PERMISSIONS;
    return {
        allowFreePlay: user.allowFreePlay ?? DEFAULT_ALLOW_FREE_PLAY,
        allowAccessToAllLimits: user.allowAccessToAllLimits ?? DEFAULT_ALLOW_ACCESS_TO_ALL_LIMITS,
        allowCreditDebitAdjustments: user.allowCreditDebitAdjustments ?? DEFAULT_ALLOW_CREDIT_DEBIT_ADJUSTMENTS,
    };
}

function hasStoredPermissions(user: Users): boolean {
    return typeof user.allowFreePlay === 'boolean'
        && typeof user.allowAccessToAllLimits === 'boolean'
        && typeof user.allowCreditDebitAdjustments === 'boolean';
}

/** Sub-agent permissions live on the user document. */
export function useAgentPermissions(user?: Users | null): AgentPermissions {
    const { updateUser } = useUser();
    const [permissions, setPermissions] = useState<AgentPermissions>(() => permissionsFromUser(user));

    useEffect(() => {
        if (!user?.userName || user.userType !== 'subAgent') {
            setPermissions(DEFAULT_AGENT_PERMISSIONS);
            return;
        }

        if (hasStoredPermissions(user)) {
            setPermissions(permissionsFromUser(user));
            return;
        }

        let cancelled = false;
        getAgentUserLimits(user.userName).then(agent => {
            if (cancelled || !agent) return;
            const next = permissionsFromUser(agent);
            setPermissions(next);
            if (!hasStoredPermissions(agent)) return;
            updateUser({
                allowFreePlay: agent.allowFreePlay,
                allowAccessToAllLimits: agent.allowAccessToAllLimits,
                allowCreditDebitAdjustments: agent.allowCreditDebitAdjustments,
                agentMaxPayout: agent.agentMaxPayout,
                agentMaxFreePlayPayout: agent.agentMaxFreePlayPayout,
                agentMaxFreePlayOdds: agent.agentMaxFreePlayOdds,
                agentMaxOverallOdds: agent.agentMaxOverallOdds,
            });
        });

        return () => {
            cancelled = true;
        };
        // updateUser writes through a functional setState, so a new identity must not refetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        user?.userName,
        user?.userType,
        user?.allowFreePlay,
        user?.allowAccessToAllLimits,
        user?.allowCreditDebitAdjustments,
    ]);

    return permissions;
}
